import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useState } from 'react';
import {
  offerKinds, type OfferKind, type OfferKindInput, type OfferKindMeta, type OfferKindSectionTpl,
  type OfferKindSectionItemTpl,
} from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';

/** Sekcje listowe mogą mieć bibliotekę pozycji (checkbox w kreatorze). */
const isListType = (rt: string) => rt === 'NUMBERED_LIST' || rt === 'BULLET_LIST';

/**
 * Admin: konfigurowalne rodzaje ofert (V89). Definiuje przedmiot (opracowanie/prace terenowe),
 * następstwo po akceptacji (umowa/zlecenie/opracowanie/brak), domyślny tryb wyceny, format numeru,
 * drugi podpis oraz szablony sekcji treści zasiewane do oferty tego rodzaju.
 */
export function OfferKindsAdmin() {
  const [rows, setRows] = useState<OfferKind[] | null>(null);
  const [meta, setMeta] = useState<OfferKindMeta | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<OfferKind | null>(null);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = () => {
    setErr(null);
    setRows(null);
    offerKinds.list(includeInactive).then(setRows).catch(e => setErr(e?.message ?? String(e)));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [includeInactive]);
  useEffect(() => { offerKinds.meta().then(setMeta).catch(() => {}); }, []);

  const onDeactivate = async (t: OfferKind) => {
    if (!confirm(`Dezaktywować rodzaj „${t.code} — ${t.name}”?`)) return;
    try {
      await offerKinds.delete(t.id);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="max-w-[1200px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Rodzaje ofert</h1>
          <div className="text-ink-400 text-[13px]">
            Konfigurowalne typy ofert. Rodzaj ustawia domyślny tryb wyceny, następstwo po akceptacji
            (umowa / zlecenie / opracowanie), format numeru, drugi podpis oraz szablony sekcji treści
            (Dane studni, Zakres prac, Zapotrzebowanie…) używane w kreatorze.
          </div>
        </div>
        <div className="flex gap-2">
          <label className="text-[12px] text-ink-700 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)}/>
            Pokaż dezaktywowane
          </label>
          <button onClick={() => setCreating(true)} className="btn btn-primary btn-sm">
            <Icon name="plus" className="w-3.5 h-3.5"/> Nowy rodzaj
          </button>
        </div>
      </header>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}

      <div className="card">
        <div className="card-body !p-0">
          {!rows && <div className="p-4 text-ink-400">Ładowanie…</div>}
          {rows && rows.length === 0 && <div className="p-4 text-ink-400">Brak rodzajów. Dodaj pierwszy.</div>}
          {rows && rows.length > 0 && (
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Kod</th>
                  <th>Nazwa</th>
                  <th>Przedmiot</th>
                  <th>Następstwo</th>
                  <th>Wycena</th>
                  <th className="w-16">Sekcje</th>
                  <th className="w-16">Otwór</th>
                  <th className="w-24">Status</th>
                  <th className="w-20"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(t => (
                  <tr key={t.id} className={t.active ? '' : 'opacity-50'}>
                    <td className="font-mono text-ink-800">{t.code}</td>
                    <td className="text-ink-900">{t.name}</td>
                    <td className="text-[12px] text-ink-600">{t.purposeName ?? t.purpose}</td>
                    <td className="text-[12px] text-ink-600">{t.followUpName ?? t.followUp}</td>
                    <td className="text-[12px] text-ink-600">{t.defaultPricingModeName ?? t.defaultPricingMode}</td>
                    <td className="text-center font-mono text-[11px] text-ink-500">{t.sections?.length ?? 0}</td>
                    <td className="text-center">{t.usesBoreholeData ? <Icon name="check" className="w-3.5 h-3.5 text-green-600 inline"/> : '—'}</td>
                    <td>{t.active
                      ? <span className="pill pill-green">aktywny</span>
                      : <span className="pill pill-amber">dezakt.</span>}</td>
                    <td className="text-right whitespace-nowrap">
                      <button onClick={() => setEditing(t)} className="text-water-600 hover:text-water-800 p-1" title="Edytuj">
                        <Icon name="edit" className="w-3.5 h-3.5"/>
                      </button>
                      {t.active && (
                        <button onClick={() => onDeactivate(t)} className="text-red-500 hover:text-red-700 p-1 ml-1" title="Dezaktywuj">
                          <Icon name="x" className="w-3.5 h-3.5"/>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {(creating || editing) && (
        <OfferKindEditModal
          initial={editing}
          meta={meta}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); reload(); }}
        />
      )}
    </div>
  );
}

function OfferKindEditModal({ initial, meta, onClose, onSaved }: {
  initial: OfferKind | null;
  meta: OfferKindMeta | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = initial != null;
  const [form, setForm] = useState<OfferKindInput>({
    code: initial?.code ?? '',
    name: initial?.name ?? '',
    abbreviation: initial?.abbreviation ?? '',
    purpose: initial?.purpose ?? 'FIELDWORK',
    followUp: initial?.followUp ?? 'ORDER',
    defaultPricingMode: initial?.defaultPricingMode ?? 'LINE_ITEMS',
    numberFormat: initial?.numberFormat ?? '',
    usesBoreholeData: initial?.usesBoreholeData ?? false,
    secondSignatureLabel: initial?.secondSignatureLabel ?? '',
    subjectTemplate: initial?.subjectTemplate ?? '',
    description: initial?.description ?? '',
    sortOrder: initial?.sortOrder ?? 100,
    active: initial?.active ?? true,
  });
  const [sections, setSections] = useState<OfferKindSectionTpl[]>(
    (initial?.sections ?? []).map(s => ({ ...s })),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const opt = (list?: { value: string; label: string }[]) => (list ?? []).map(o => ({ value: o.value, label: o.label }));

  const addSection = () => setSections([...sections, {
    heading: '', renderType: 'RICH_TEXT', defaultContent: '', active: true,
    sortOrder: (sections.length ? Math.max(...sections.map(s => s.sortOrder ?? 0)) : 0) + 10,
  }]);
  const patchSection = (i: number, patch: Partial<OfferKindSectionTpl>) =>
    setSections(sections.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const removeSection = (i: number) => setSections(sections.filter((_, idx) => idx !== i));
  const moveSection = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[i], next[j]] = [next[j], next[i]];
    setSections(next);
  };

  // --- Pozycje biblioteki sekcji (checkbox) ---
  const patchItems = (si: number, items: OfferKindSectionItemTpl[]) => patchSection(si, { items });
  const addItem = (si: number) => {
    const items = sections[si].items ?? [];
    patchItems(si, [...items, { text: '', defaultChecked: true, active: true }]);
  };
  const patchItem = (si: number, ii: number, patch: Partial<OfferKindSectionItemTpl>) => {
    const items = (sections[si].items ?? []).map((it, idx) => idx === ii ? { ...it, ...patch } : it);
    patchItems(si, items);
  };
  const removeItem = (si: number, ii: number) =>
    patchItems(si, (sections[si].items ?? []).filter((_, idx) => idx !== ii));
  const moveItem = (si: number, ii: number, dir: -1 | 1) => {
    const items = [...(sections[si].items ?? [])];
    const j = ii + dir;
    if (j < 0 || j >= items.length) return;
    [items[ii], items[j]] = [items[j], items[ii]];
    patchItems(si, items);
  };

  const save = async () => {
    if (!form.code?.trim()) { setErr('Kod jest wymagany'); return; }
    if (!form.name?.trim()) { setErr('Nazwa jest wymagana'); return; }
    setBusy(true); setErr(null);
    try {
      const payload: OfferKindInput = {
        ...form,
        sections: sections.map((s, i) => ({
          ...s,
          sortOrder: (i + 1) * 10,
          // Biblioteka pozycji tylko dla sekcji listowych; pomijamy puste teksty.
          items: isListType(s.renderType)
            ? (s.items ?? [])
                .filter(it => (it.text ?? '').trim())
                .map((it, j) => ({ ...it, sortOrder: (j + 1) * 10 }))
            : [],
        })),
      };
      if (isEdit && initial) await offerKinds.update(initial.id, payload);
      else await offerKinds.create(payload);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const renderTypeHint = (rt: string) =>
    rt === 'SPEC_TABLE' ? 'Domyślnie: „Klucz: Wartość” w wierszu (auto-uzupełniane z otworu w kreatorze)'
      : rt === 'NUMBERED_LIST' || rt === 'BULLET_LIST' ? 'Domyślnie: jedna pozycja w wierszu'
      : 'Treść jako HTML — do wykazów użyj typów listowych (łamania linii w zwykłym tekście znikają)';

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { form, sections }, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div className="bg-white rounded-lg shadow-2xl max-w-[820px] w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="border-b border-ink-100 px-5 py-3 flex items-center gap-3 sticky top-0 bg-white z-10">
          <h2 className="text-[16px] font-semibold m-0">{isEdit ? 'Edycja rodzaju oferty' : 'Nowy rodzaj oferty'}</h2>
          <button onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x"/></button>
        </div>

        <div className="p-5 space-y-3">
          {err && <div className="text-red-500 text-[12px]">{err}</div>}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Kod (np. REMONT)*">
              <input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} className="input font-mono"/>
            </Field>
            <Field label="Nazwa*">
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="np. Remont studni" className="input"/>
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Przedmiot">
              <SearchableSelect value={form.purpose ?? 'FIELDWORK'} onChange={v => setForm({ ...form, purpose: v })} options={opt(meta?.purposes)}/>
            </Field>
            <Field label="Następstwo akceptacji">
              <SearchableSelect value={form.followUp ?? 'ORDER'} onChange={v => setForm({ ...form, followUp: v })} options={opt(meta?.followUps)}/>
            </Field>
            <Field label="Domyślna wycena">
              <SearchableSelect value={form.defaultPricingMode ?? 'LINE_ITEMS'} onChange={v => setForm({ ...form, defaultPricingMode: v })} options={opt(meta?.pricingModes)}/>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Format numeru (opcjonalny, np. {YYYY}/{MM}/{N:3}/O/T)">
              <input value={form.numberFormat ?? ''} onChange={e => setForm({ ...form, numberFormat: e.target.value })} placeholder="puste = globalny format ofert" className="input font-mono text-[12px]"/>
            </Field>
            <Field label="Etykieta drugiego podpisu (opcjonalna)">
              <input value={form.secondSignatureLabel ?? ''} onChange={e => setForm({ ...form, secondSignatureLabel: e.target.value })} placeholder="np. Osoba uprawniona do odbioru" className="input"/>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3 items-center">
            <label className="flex items-center gap-2 text-[12px] text-ink-700 cursor-pointer">
              <input type="checkbox" checked={form.usesBoreholeData ?? false} onChange={e => setForm({ ...form, usesBoreholeData: e.target.checked })}/>
              Korzysta z „Danych studni” (powiązanie z otworem)
            </label>
            <Field label="Kolejność">
              <input type="number" value={form.sortOrder ?? 100} onChange={e => setForm({ ...form, sortOrder: Number(e.target.value) })} className="input font-mono w-28"/>
            </Field>
          </div>

          <Field label="Szablon przedmiotu „Na wykonanie…” (auto-uzupełniany w kreatorze)">
            <textarea value={form.subjectTemplate ?? ''} onChange={e => setForm({ ...form, subjectTemplate: e.target.value })}
                      className="input min-h-[52px] text-[12px]" rows={2}
                      placeholder={'np. Na wykonanie regeneracji studni głębinowej{studnia} na ujęciu wód podziemnych „{ujecie}”{miejscowosc}.'}/>
            <span className="text-[10px] text-ink-400">
              {'Placeholdery: {ujecie} — nazwa ujęcia · {miejscowosc} — „ w miejscowości X” · {studnia} — „ nr {oznaczenie otworu}”. Puste znikają.'}
            </span>
          </Field>

          <Field label="Opis (opcjonalny)">
            <textarea value={form.description ?? ''} onChange={e => setForm({ ...form, description: e.target.value })} className="input min-h-[52px]" rows={2}/>
          </Field>

          {/* Szablony sekcji */}
          <div className="border-t border-ink-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">Szablony sekcji treści</span>
            </div>
            {sections.length === 0 && <div className="text-[12px] text-ink-400 py-2">Brak sekcji — rodzaj bez dodatkowych bloków treści.</div>}
            <div className="space-y-2">
              {sections.map((s, i) => (
                <div key={i} className="border border-ink-150 rounded-md p-2.5 bg-ink-50/40">
                  <div className="flex items-center gap-2 mb-1.5">
                    <input value={s.heading ?? ''} onChange={e => patchSection(i, { heading: e.target.value })} placeholder="Nagłówek sekcji (np. Zakres prac)" className="input flex-1 !py-1"/>
                    <div className="w-52">
                      <SearchableSelect value={s.renderType} onChange={v => patchSection(i, { renderType: v })} options={opt(meta?.renderTypes)}/>
                    </div>
                    <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="text-ink-400 hover:text-ink-700 disabled:opacity-30 p-1" title="W górę"><Icon name="chevron-up" className="w-3.5 h-3.5"/></button>
                    <button onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1} className="text-ink-400 hover:text-ink-700 disabled:opacity-30 p-1" title="W dół"><Icon name="chevron-down" className="w-3.5 h-3.5"/></button>
                    <button onClick={() => removeSection(i)} className="text-red-500 hover:text-red-700 p-1" title="Usuń"><Icon name="x" className="w-3.5 h-3.5"/></button>
                  </div>
                  {isListType(s.renderType) ? (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">
                          Pozycje do odhaczenia w kreatorze
                        </span>
                      </div>
                      {(s.items ?? []).length === 0 && (
                        <div className="text-[11px] text-ink-400 py-1">
                          Brak pozycji — w kreatorze sekcja będzie zwykłym polem tekstowym.
                        </div>
                      )}
                      <div className="space-y-1">
                        {(s.items ?? []).map((it, ii) => (
                          <div key={ii} className="flex items-center gap-1.5">
                            <input type="checkbox" checked={it.defaultChecked ?? true}
                                   onChange={e => patchItem(i, ii, { defaultChecked: e.target.checked })}
                                   title="Domyślnie zaznaczone w kreatorze" className="shrink-0"/>
                            <input value={it.text ?? ''} onChange={e => patchItem(i, ii, { text: e.target.value })}
                                   placeholder="Treść pozycji (np. Inspekcja TV.)" className="input flex-1 !py-0.5 text-[12px]"/>
                            <button onClick={() => moveItem(i, ii, -1)} disabled={ii === 0} className="text-ink-400 hover:text-ink-700 disabled:opacity-30 p-0.5" title="W górę"><Icon name="chevron-up" className="w-3 h-3"/></button>
                            <button onClick={() => moveItem(i, ii, 1)} disabled={ii === (s.items ?? []).length - 1} className="text-ink-400 hover:text-ink-700 disabled:opacity-30 p-0.5" title="W dół"><Icon name="chevron-down" className="w-3 h-3"/></button>
                            <button onClick={() => removeItem(i, ii)} className="text-red-500 hover:text-red-700 p-0.5" title="Usuń"><Icon name="x" className="w-3 h-3"/></button>
                          </div>
                        ))}
                      </div>
                      <button onClick={() => addItem(i)} className="text-water-600 hover:text-water-800 text-[11px] mt-1">
                        <Icon name="plus" className="w-3 h-3 inline"/> Dodaj pozycję
                      </button>
                      <div className="text-[10px] text-ink-400 mt-1">
                        ☑ = pozycja domyślnie zaznaczona. W kreatorze można odznaczyć lub dopisać własne pozycje.
                      </div>
                    </div>
                  ) : (
                    <textarea value={s.defaultContent ?? ''} onChange={e => patchSection(i, { defaultContent: e.target.value })} placeholder={renderTypeHint(s.renderType)} className="input w-full !py-1 text-[12px]" rows={2}/>
                  )}
                </div>
              ))}
            </div>
            <button onClick={addSection} className="btn btn-secondary btn-sm mt-2"><Icon name="plus" className="w-3 h-3"/> Dodaj sekcję</button>
          </div>

          {isEdit && (
            <label className="flex items-center gap-2 text-[12px] text-ink-700 cursor-pointer">
              <input type="checkbox" checked={form.active ?? true} onChange={e => setForm({ ...form, active: e.target.checked })}/>
              Aktywny (widoczny w kreatorze ofert)
            </label>
          )}
        </div>

        <div className="border-t border-ink-100 px-5 py-3 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={requestClose} disabled={busy} className="btn btn-secondary btn-sm">Anuluj</button>
          <button onClick={save} disabled={busy} className="btn btn-primary btn-sm">
            {busy ? 'Zapisywanie…' : (isEdit ? 'Zapisz zmiany' : 'Dodaj rodzaj')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">{label}</span>
      {children}
    </div>
  );
}
